import { Injectable, InternalServerErrorException, NotFoundException } from '@nestjs/common';
import {
  LocalDiskImageStorageAdapter,
  PrismaService,
  S3CompatibleAdapter,
  StorageAdapter,
} from '@cart/api-data-access';
import { readPlatformStorageEnv, readPresignCredentials } from './platform-storage.constants';
import { DecryptPurpose, StorageCredentialVault } from './storage-credential.vault';

interface CachedAdapter {
  adapter: StorageAdapter;
  /** Config rows are immutable in the fields that matter except on rotation,
   *  which bumps updatedAt — so this doubles as the cache version. */
  version: number;
}

export interface ResolvedConfig {
  id: string;
  organizationId: string;
  mode: string;
  status: string;
  bucket: string;
  region: string;
  endpoint: string | null;
  publicBaseUrl: string | null;
  pathPrefix: string | null;
  updatedAt: Date;
}

/**
 * Turns a storage config row into a usable adapter.
 *
 * Resolution is keyed on **storageConfigId, not organizationId** — that is the
 * whole mechanism behind switching storage without moving data
 * (docs/19-media-management-plan.md §5). An asset written on day 1 carries the
 * config it was written to, so a delete or a read for that asset resolves the
 * old backend even though the org's active target is now somewhere else.
 */
@Injectable()
export class StorageAdapterResolver {
  private readonly cache = new Map<string, CachedAdapter>();

  constructor(
    private readonly prisma: PrismaService,
    private readonly vault: StorageCredentialVault
  ) {}

  async configById(storageConfigId: string): Promise<ResolvedConfig> {
    const config = await this.prisma.merchantStorageConfig.findUnique({
      where: { id: storageConfigId },
      select: {
        id: true,
        organizationId: true,
        mode: true,
        status: true,
        bucket: true,
        region: true,
        endpoint: true,
        publicBaseUrl: true,
        pathPrefix: true,
        updatedAt: true,
      },
    });
    if (!config) throw new NotFoundException(`Storage config ${storageConfigId} not found`);
    return config;
  }

  /**
   * The org's current write target. Callers that are writing use this; callers
   * that are reading or deleting an existing asset must use `forConfig` with
   * the asset's own config instead.
   */
  async activeConfig(organizationId: string): Promise<ResolvedConfig | null> {
    return this.prisma.merchantStorageConfig.findFirst({
      where: { organizationId, status: 'active' },
      select: {
        id: true,
        organizationId: true,
        mode: true,
        status: true,
        bucket: true,
        region: true,
        endpoint: true,
        publicBaseUrl: true,
        pathPrefix: true,
        updatedAt: true,
      },
    });
  }

  async forConfigId(storageConfigId: string, purpose: DecryptPurpose): Promise<StorageAdapter> {
    return this.forConfig(await this.configById(storageConfigId), purpose);
  }

  async forConfig(config: ResolvedConfig, purpose: DecryptPurpose): Promise<StorageAdapter> {
    const version = config.updatedAt.getTime();
    const cached = this.cache.get(config.id);
    if (cached && cached.version === version) return cached.adapter;

    const adapter = await this.build(config, purpose);
    this.cache.set(config.id, { adapter, version });
    return adapter;
  }

  /** Called on credential rotation and on any config edit. */
  invalidate(storageConfigId: string): void {
    this.cache.delete(storageConfigId);
  }

  invalidateAll(): void {
    this.cache.clear();
  }

  private async build(config: ResolvedConfig, purpose: DecryptPurpose): Promise<StorageAdapter> {
    if (config.mode === 'local_disk') {
      // Migration source only — see LocalDiskImageStorageAdapter's header.
      return new LocalDiskImageStorageAdapter();
    }

    if (config.mode === 'platform_r2') {
      const env = readPlatformStorageEnv();
      if (!env) {
        throw new InternalServerErrorException(
          'Platform storage is not configured (PLATFORM_R2_* environment variables missing).'
        );
      }
      // Presigning uses the narrower upload-only key so the key ID exposed in
      // the URL query string is not the one with full bucket rights.
      const credentials = purpose === 'presign' ? readPresignCredentials(env) : env;
      return new S3CompatibleAdapter({
        bucket: env.bucket,
        region: env.region,
        endpoint: env.endpoint,
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        publicBaseUrl: env.publicBaseUrl,
        pathPrefix: config.pathPrefix ?? undefined,
        forcePathStyle: true,
      });
    }

    return this.vault.adapterFor(config, purpose);
  }
}
